import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, AlertTriangle, Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const ALARM_STATUSES = new Set(["Suspended", "Excluded", "Deceased", "Inactive", "Dormant"]);

export default function GlobalMembersPage() {
  const { t, i18n } = useTranslation();
  const [grades, setGrades] = useState({});
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reference/grades");
        const map = {};
        data.forEach((g) => {
          map[g.degree] = i18n.language !== "ro" ? g.name_en : g.name_ro;
        });
        setGrades(map);
      } catch { /* ignore */ }
    })();
  }, [i18n.language]);

  const search = async (e) => {
    e?.preventDefault();
    const term = q.trim();
    setError("");
    if (!term) {
      setResults([]);
      setSubmittedQ("");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get(`/global-members/search?q=${encodeURIComponent(term)}`);
      setResults(data);
      setSubmittedQ(term);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const gradeLabel = (m) => {
    const num = m.degree;
    const name = grades[num] || m.grade || "—";
    return num ? `${num}° ${name}` : name;
  };

  return (
    <div data-testid="global-members-page">
      <div>
        <h1 className="page-title">{t("globalMembers.title")}</h1>
        <p className="page-subtitle">{t("globalMembers.subtitle")}</p>
      </div>
      <div className="gold-divider my-6" />

      <form onSubmit={search} className="flex gap-2 mb-6 flex-wrap">
        <input
          type="search"
          className="input-lodge"
          style={{ flex: 1, minWidth: 240 }}
          placeholder={t("globalMembers.placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="global-members-search-input"
        />
        <button type="submit" className="btn-primary-gold flex items-center gap-1" disabled={busy} data-testid="global-members-search-btn">
          <Search size={14} /> {busy ? "..." : t("globalMembers.searchBtn")}
        </button>
      </form>

      {error && <div className="error-banner mb-4">{error}</div>}

      {submittedQ && results.length === 0 && (
        <div
          data-testid="profane-banner"
          style={{
            background: "#7c2a2a",
            color: "#fff",
            padding: "1.6rem 1.8rem",
            border: "3px solid #5a1212",
            textAlign: "center",
          }}
        >
          <AlertTriangle size={42} style={{ display: "block", margin: "0 auto 0.6rem" }} />
          <div
            style={{
              fontFamily: "Cinzel, serif",
              fontSize: "1.05rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            {t("globalMembers.profane")}
          </div>
          <div className="kv-label" style={{ color: "rgba(255,255,255,0.75)", marginTop: "0.4rem" }}>
            "{submittedQ}"
          </div>
        </div>
      )}

      {results.length > 0 && (
        <table className="lodge-table" data-testid="global-members-results">
          <thead>
            <tr>
              <th>{t("members.name", "Nume")}</th>
              <th>{t("members.grade", "Grad")}</th>
              <th>Status</th>
              <th>{t("globalMembers.homeLodge")}</th>
              <th>{t("members.passportNumber", "Pașaport")}</th>
            </tr>
          </thead>
          <tbody>
            {results.map((m) => {
              const isAlarm = ALARM_STATUSES.has(m.status);
              const isPrivate = !!m.is_private;
              return (
                <tr key={m.id} data-testid={`global-member-${m.id}`} style={isAlarm ? { background: "#fdecec" } : isPrivate ? { background: "#fbf7ec" } : undefined}>
                  <td>
                    <div style={{ fontFamily: "Cinzel, serif", color: isAlarm ? "#7c2a2a" : "#0d1b2a", fontSize: "0.95rem" }}>
                      {m.last_name} {m.first_name}
                      {isAlarm && (
                        <AlertTriangle size={13} color="#7c2a2a" style={{ display: "inline", marginLeft: 6, verticalAlign: "-2px" }} />
                      )}
                      {isPrivate && !isAlarm && (
                        <Lock size={12} color="#9a7530" style={{ display: "inline", marginLeft: 6, verticalAlign: "-2px" }} aria-label="Membru privat" />
                      )}
                    </div>
                    {isAlarm && m.status_reason && (
                      <div className="kv-label" style={{ color: "#7c2a2a", fontStyle: "italic", marginTop: 2 }}>
                        {m.status_reason}
                      </div>
                    )}
                    {isPrivate && (
                      <div className="kv-label" style={{ color: "#9a7530", fontStyle: "italic", marginTop: 2 }}>
                        {t("globalMembers.private", "Membru privat — date ascunse")}
                      </div>
                    )}
                  </td>
                  <td>{gradeLabel(m)}</td>
                  <td>
                    <span
                      className="badge-status"
                      style={{
                        color: isAlarm ? "#7c2a2a" : "#3e6a3c",
                        background: isAlarm ? "#f6e0e0" : "#e8f0e5",
                        border: `1px solid ${isAlarm ? "#d9b9b9" : "#b9cfb4"}`,
                        fontSize: "0.62rem",
                      }}
                    >
                      {m.status || "—"}
                    </span>
                  </td>
                  <td>{m.home_lodge_name}</td>
                  <td className="kv-label">{isPrivate ? "•••" : (m.passport_number || "—")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
